import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../../constants/AppTable';
import { IssueProduct, Product, TransactionPurchase } from '../../../../helpers/models/Models';
import { LoadingSpinner } from '../../../../constants/AppLoading';
import { CountNepaliDays } from '../../../../helpers/DateFormat';
import { NepNowDateFormat } from '../../../../constants/DateFormat';
import { useFormik } from 'formik';
import { putDataToTheServer } from '../../../../helpers/api';
import AppURL from '../../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../../constants/Toast';
import DialogBox from '../../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../../constants/InputTextBox';
import { EndBaseLine } from '../../../../constants/AppSpace';
import { Button } from '../../../../constants/Button';
import DefaultSelectInputBox from '../../../../constants/DefaultSelectBox';
import { YesNoOptions } from '../../../../constants/Options';

interface IssueProductViewProps {
    data: {
        results: IssueProduct[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
}
const ProductIssueListReportView: React.FC<IssueProductViewProps> = ({ data, loading, error, handleFormSuccess, setOnURL }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<IssueProduct | null>(null);
    const nepDate = NepNowDateFormat();
    const columns = [
        { header: 'Member Category Name', accessor: 'mem_category_name', sortable: false },
        { header: 'Product Name', accessor: 'product_name', sortable: true },
        { header: 'Product Category Name', accessor: 'product_category_name', sortable: true },
        { header: 'Purchase QTY', accessor: 'purchase_qty', sortable: false },
        { header: 'Member Name', accessor: 'mem_name', sortable: false },
        { header: 'Address', accessor: 'add_eng', sortable: false },
        { header: 'Member Ph. No', accessor: 'mem_phone_no', sortable: false },
        { header: 'Store Name', accessor: 'store_name', sortable: false },
        { header: 'Store Ph. No', accessor: 'store_phone_no', sortable: false },
        { header: 'Returned', accessor: 'return', sortable: false },
        { header: 'Issue Date', accessor: 'pur_sale_date', sortable: false },
        { header: 'Return Date', accessor: 'due_date', sortable: false },
        { header: 'Actions', accessor: 'actions', action: true },
    ];
    
    const modifiedData = useMemo(() => {
        if (!data) return [];
        return data.results.map((item: IssueProduct) => ({
            sn: item.sn,
            tsn: item.tsn,
            sale_issue: item.sale_issue,
            mem_name: item?.mem_name,
            add_eng: item.add_eng,
            purchase_qty: `${item.issue_qty} ${item.sales_unit_name}`,
            mem_phone_no: item.mem_phone_no,
            store_name: item.store_name,
            pur_sale_date: item.pur_sale_date,
            store_phone_no: item.store_phone_no,
            due_dates: CountNepaliDays(item.due_date, nepDate),
            due_date: item.due_date,
            product_name: item.product_name,
            remarks: item.remarks,
            member_sn: item.member_sn,
            return: item.returned ===0?"No":"Yes",
            returned: item.returned,
            active: item.returned,
            issue_qty: item.issue_qty,
            mem_category_name: item.mem_category_name,
            sales_unit_name: item.sales_unit_name,
            product_category_name: item.product_category_name,
        }));
    }, [data]);

    const handleEdit = (item: IssueProduct) => {
        setCurrentItem(item);
        setDialogOpen(true);
    };
    const formik = useFormik({
        initialValues: {
            product_name: currentItem?.product_name || "",
            sn: currentItem?.sn || "",
            remarks: currentItem?.remarks || "",
            tsn: currentItem?.tsn || "",
            pur_sale_date: currentItem?.pur_sale_date || "",
            returned: currentItem?.returned === 0 ? 2 : 1 || "",
            mem_name: currentItem?.mem_name || "",
            add_eng: currentItem?.add_eng ||"",
            mem_category_name: currentItem?.mem_category_name ||"",
            issue_qty: currentItem?.issue_qty ||"",
            store_name: currentItem?.store_name ||"",

        },
        // validationSchema: readerEditValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            formData.append("returned", values.returned?.toString()=== "1" ? "1" : "0");
            formData.append("remarks", values.remarks);
            try {
                await putDataToTheServer(AppURL.InventoryURL + 'inventory/product/transaction/update/view/' + currentItem?.sn, formData).then((res) => {
                    if (res?.status === 200) {
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    } else if (res?.status === 400) {
                        ToastWarning({ message: res?.message });
                    }
                    else {
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });
    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }

    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onNext={() => data?.next && setOnURL(data.next)}
                onEdit={handleEdit}
                onPrev={() => data?.previous && setOnURL(data.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Product Issue/Return Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={0.8}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='mem_category_name'
                                    label='Master Category'
                                    handleChange={formik.handleChange}
                                    value={formik.values.mem_category_name}
                                    error={typeof formik.errors.mem_category_name === 'string' ? formik.errors.mem_category_name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.mem_category_name}
                                    required={true}
                                    disabled
                                    placeholder='Enter Master Category'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='product_name'
                                    label='Product Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.product_name}
                                    error={typeof formik.errors.product_name === 'string' ? formik.errors.product_name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.product_name}
                                    required={true}
                                    disabled
                                    placeholder='Enter Product Name'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='issue_qty'
                                    label='Issue QTY'
                                    handleChange={formik.handleChange}
                                    value={formik.values.issue_qty}
                                    error={typeof formik.errors.issue_qty === 'string' ? formik.errors.issue_qty : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.issue_qty}
                                    required={true}
                                    disabled
                                    placeholder='Enter Product Issue QTY'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='pur_sale_date'
                                    label='Issue Date'
                                    handleChange={formik.handleChange}
                                    value={formik.values.pur_sale_date}
                                    error={typeof formik.errors.pur_sale_date === 'string' ? formik.errors.pur_sale_date : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.pur_sale_date}
                                    required={true}
                                    disabled
                                    placeholder='Enter Issue Date'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='store_name'
                                    label='Store Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.store_name}
                                    error={typeof formik.errors.store_name === 'string' ? formik.errors.store_name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.store_name}
                                    required={true}
                                    disabled
                                    placeholder='Enter Store Name'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <DefaultSelectInputBox
                                    name="returned"
                                    label='Returned'
                                    disabled={false} // Adjust based on your needs
                                    options={YesNoOptions}
                                    value={formik.values.returned}
                                    optionSelect={(name, selectedOption) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }}
                                    required
                                    placeholder="Select Returned Status"
                                />
                            </Grid>
                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='remarks'
                                    label='Remarks'
                                    handleChange={formik.handleChange}
                                    value={formik.values.remarks}
                                    error={typeof formik.errors.remarks === 'string' ? formik.errors.remarks : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.remarks}
                                    required={false}
                                    placeholder='Enter Remarks'
                                />
                            </Grid>
                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button btn_type='update' type='submit' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    )
}

export default ProductIssueListReportView